// Authorize.Net Subscriptions — a thin config over the shared SubscriptionsWorkspace (same shell as
// PaySimple). This file supplies only AuthNet data: endpoints, stat tiles, columns, the check-failures
// header action, and its three dialogs (payment history / backfill / failures).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Button, Table, TableBody, TableCell, TableHead, TableRow, Chip, CircularProgress,
  Alert, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip, Checkbox, FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import SubscriptionsWorkspace from '../../components/recon/SubscriptionsWorkspace';
import CreatedOrderReceipts from '../../components/recon/CreatedOrderReceipts';
import DonorLink from '../../components/recon/DonorLink';
import { buildAuthnetNav } from '../../config/recon/authnetNav';
import { authnetMapApi } from '../../config/recon/authnet';
import { get, post } from '../../utils/api';
import { fmtMoney, ACCOUNT_LABEL } from '../../components/recon/reconShared';

const STATUS_OPTIONS = [
  { value: 'active', label: 'In progress' },
  { value: 'expired', label: 'Expired' },
  { value: 'terminated', label: 'Terminated' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'all', label: 'All statuses' },
];
const occLabel = (s) => `${(s.stats?.paid_count ?? s.past_occurrences) ?? 0} of ${s.total_occurrences == null ? '∞' : s.total_occurrences}`;
const normStatus = (s) => { const x = String(s || '').toLowerCase(); if (/cancel/.test(x)) return 'canceled'; if (/terminat/.test(x)) return 'terminated'; if (/expir/.test(x)) return 'expired'; if (/suspend/.test(x)) return 'suspended'; if (/active/.test(x)) return 'active'; return x || 'unknown'; };
const statusChipColor = (s) => { const x = normStatus(s); return x === 'active' ? 'success' : x === 'terminated' ? 'error' : x === 'canceled' || x === 'suspended' ? 'warning' : 'default'; };
const statusChipLabel = (s) => { const x = normStatus(s); return x === 'active' ? 'In progress' : x === 'expired' ? 'Expired' : x.charAt(0).toUpperCase() + x.slice(1); };
const expiredInfo = (s) => {
  const total = s.total_occurrences == null ? null : Number(s.total_occurrences);
  const paid = Number(s.stats?.paid_count ?? 0);
  const problems = Number(s.stats?.problem_count || 0);
  const missing = total == null ? null : Math.max(0, total - paid);
  const complete = total != null && missing === 0 && problems === 0;
  return { total, paid, problems, missing, complete };
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');
const failCount = (s, ctx) => ctx.extra?.by_sub?.[`${s.account}:${s.id}`] || 0;
const failRowsFor = (s, ctx) => (ctx.extra?.failures || [])
  .filter((f) => f.account === s.account && String(f.subscription_id) === String(s.id))
  .sort((a, b) => new Date(b.submitted_at || 0) - new Date(a.submitted_at || 0));

function PaymentHistoryDialog({ sub, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (!sub) return undefined;
    let cancel = false;
    (async () => {
      setLoading(true); setErr(''); setData(null);
      try { const r = await get('/api/manage/authnet-subscriptions/payments', { account: sub.account, subscription_id: sub.id }); if (!cancel) setData(r); }
      catch (e) { if (!cancel) setErr(e?.response?.data?.error || e?.message || 'Failed to load payment history'); }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [sub]);
  const st = data?.stats || {};
  return (
    <Dialog open={!!sub} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', pr: 1 }}>
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>Payment history</Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{sub?.name || '(unnamed)'} <Typography component="span" variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{sub?.id}</Typography></Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          : err ? <Alert severity="error">{err}</Alert>
            : data ? (
              <>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                  <Chip size="small" color={statusChipColor(data.status)} label={statusChipLabel(data.status)} />
                  <Chip size="small" variant="outlined" label={`${fmtMoney(data.amount)} / installment`} />
                  <Chip size="small" color="success" variant="outlined" label={`${st.paid_count || 0} paid`} />
                  {st.pending != null && <Chip size="small" variant="outlined" label={`${st.pending} pending`} />}
                  {st.failed_count > 0 && <Chip size="small" color="error" icon={<WarningAmberIcon sx={{ fontSize: 14 }} />} label={`${st.failed_count} failed`} />}
                </Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
                  Last paid: {fmtDate(st.last_paid_date)}{st.last_pay_num ? ` · installment #${st.last_pay_num}` : ''}{data.total_occurrences ? ` · ${data.total_occurrences} scheduled` : ' · ongoing'}
                </Typography>
                <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                  {(data.payments || []).length === 0 && <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}><Typography variant="caption">No charge attempts recorded.</Typography></Box>}
                  {(data.payments || []).map((p, i) => (
                    <Box key={p.trans_id || i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, px: 1.25, py: 0.75, borderBottom: 1, borderColor: 'divider', bgcolor: p.approved ? 'transparent' : '#fdecec' }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>#{p.pay_num ?? '?'}{p.attempt > 1 ? ` (attempt ${p.attempt})` : ''} · {fmtDate(p.date)}</Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: p.approved ? 'text.secondary' : 'error.main' }}>{p.approved ? 'Approved' : 'Declined'}{p.response ? ` — ${p.response}` : ''}</Typography>
                        {p.order_number ? <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>Order {p.order_number}</Typography>
                          : p.approved ? <Typography variant="caption" sx={{ display: 'block', color: 'warning.main' }}>No order booked yet</Typography> : null}
                      </Box>
                      <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>{fmtMoney(p.amount)}</Typography>
                    </Box>
                  ))}
                </Box>
              </>
            ) : null}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function BackfillDialog({ sub, onClose, onDone }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [linkingTx, setLinkingTx] = useState('');
  const [linkMsg, setLinkMsg] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const load = useCallback(async () => {
    if (!sub) return;
    setLoading(true); setErr('');
    try { const r = await get('/api/manage/authnet-subscriptions/backfill-preview', { account: sub.account, id: sub.id }); setData(r); setSelected(new Set((r?.create || []).map((c) => String(c.trans_id)))); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Could not load preview'); }
    finally { setLoading(false); }
  }, [sub]);
  useEffect(() => { if (sub) { setResult(null); setLinkMsg(''); setData(null); setSelected(new Set()); load(); } }, [sub, load]);
  const run = async () => {
    setRunning(true); setErr('');
    try { const r = await post('/api/manage/authnet-subscriptions/backfill', { account: sub.account, subscription_id: sub.id, trans_ids: Array.from(selected) }); setResult(r); if (onDone) onDone(); }
    catch (e) { setErr(e?.response?.data?.error || e?.message || 'Create failed'); }
    finally { setRunning(false); }
  };
  const linkOne = async (transId, orderId, orderNumber) => {
    setLinkingTx(transId); setErr('');
    try {
      await post('/api/manage/authnet-reconcile/link', { trans_id: transId, account: sub.account, order_id: orderId });
      setLinkMsg(`Linked to #${orderNumber}`); if (onDone) onDone(); await load();
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Link failed'); }
    finally { setLinkingTx(''); }
  };
  const confColor = (c) => (c === 'high' ? 'success' : c === 'medium' ? 'warning' : 'default');
  const t = data?.totals;
  const suggest = data?.suggest_link || [];
  const nothingToDo = data && (t?.create_count || 0) === 0 && suggest.length === 0;
  const dateByTrans = {};
  (data?.create || []).forEach((c) => { dateByTrans[String(c.trans_id)] = c.date; });
  const createIds = (data?.create || []).map((c) => String(c.trans_id));
  const allChecked = createIds.length > 0 && selected.size === createIds.length;
  const someChecked = selected.size > 0 && selected.size < createIds.length;
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(createIds));
  const toggleOne = (id) => setSelected((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  return (
    <Dialog open={!!sub} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
        <Box>
          <Typography variant="overline" sx={{ color: 'text.secondary' }}>Backfill / link orders for this subscription</Typography>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{sub?.name}</Typography>
        </Box>
        <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress size={24} /></Box>
          : result ? (
            <Box>
              <Alert severity="success" sx={{ mb: 1.5 }}>Created <b>{result.created_count}</b> order(s){result.linked ? `, linked ${result.linked}` : ''}.</Alert>
              {Array.isArray(result.failed) && result.failed.length > 0 && (
                <Alert severity="warning">{result.failed.length} failed: {result.failed.slice(0, 5).map((f) => `${f.trans_id} (${f.error})`).join('; ')}</Alert>
              )}
              {result.created && result.created.length > 0 && (
                <CreatedOrderReceipts orders={result.created.map((c) => ({
                  order_number: c.order_number, amount: c.amount,
                  date: dateByTrans[String(c.trans_id)], note: c.already ? 'already existed' : undefined,
                }))} />
              )}
            </Box>
          ) : data ? (
            <Box>
              {err && <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setErr('')}>{err}</Alert>}
              {linkMsg && <Alert severity="success" sx={{ mb: 1.5 }} onClose={() => setLinkMsg('')}>{linkMsg}</Alert>}
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                {suggest.length > 0 && <Chip color="info" label={`Link ${suggest.length} to existing order(s)`} />}
                <Chip color={t.create_count > 0 ? 'success' : 'default'} variant={t.create_count > 0 ? 'filled' : 'outlined'} label={`Create ${t.create_count} · ${fmtMoney(t.create_amount)}`} />
                {t.link_count > 0 && <Chip variant="outlined" label={`${t.link_count} already linked`} />}
                {t.skip_count > 0 && <Chip color="warning" variant="outlined" label={`${t.skip_count} skipped (amount mismatch)`} />}
              </Box>
              {t.prior_year_count > 0 && (
                <Alert severity="info" sx={{ mb: 1.5 }}>{t.prior_year_count} payment(s) from prior years aren&apos;t bulk-created here — open the subscription and use <b>Create order</b> on each to pick the right year&apos;s product.</Alert>
              )}
              {suggest.length > 0 && (
                <>
                  <Typography variant="overline" sx={{ color: 'text.secondary' }}>Suggested links — these charges look already booked</Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>
                    An existing completed order matches each charge (same devotee, ±10% amount, ±3 days). Review and link each — nothing links automatically. Linking stamps the tx id on the order (no new order, no duplicate).
                  </Typography>
                  <Box sx={{ maxHeight: 240, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, mb: 1.5 }}>
                    {suggest.map((s) => (
                      <Box key={s.trans_id} sx={{ px: 1.25, py: 0.75, borderBottom: 1, borderColor: 'divider' }}>
                        <Typography variant="caption" sx={{ fontWeight: 600 }}>{s.date} · pay {s.pay_num ?? '?'} · {fmtMoney(s.amount)} <span style={{ fontFamily: 'monospace', color: '#888' }}>{s.trans_id}</span></Typography>
                        {s.candidates.map((c) => (
                          <Box key={c.order_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 0.3, pl: 1 }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography variant="caption" sx={{ display: 'block' }}>→ #{c.order_number} · {c.date} · {fmtMoney(c.total)} <Chip size="small" color={confColor(c.confidence)} label={`${c.confidence}${c.day_diff ? ` · ±${c.day_diff}d` : ''}`} sx={{ height: 16, '& .MuiChip-label': { px: 0.6, fontSize: 10 } }} /></Typography>
                              {c.items && <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }} noWrap title={c.items}>{c.items}</Typography>}
                            </Box>
                            <Button size="small" variant="outlined" onClick={() => linkOne(s.trans_id, c.order_id, c.order_number)} disabled={!!linkingTx}>{linkingTx === s.trans_id ? '…' : 'Link'}</Button>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </>
              )}
              {t.create_count > 0 && (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                    <Typography variant="overline" sx={{ color: 'text.secondary' }}>Create new orders — no existing match found</Typography>
                    <FormControlLabel sx={{ mr: 0 }} control={<Checkbox size="small" checked={allChecked} indeterminate={someChecked} onChange={toggleAll} />} label={<Typography variant="caption">Select all</Typography>} />
                  </Box>
                  <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary', mb: 0.5 }}>Each becomes a completed order, back-dated to the tx date, with the mapped allocation + a fee line and an internal note. Tick the ones to book.</Typography>
                  <Box sx={{ maxHeight: 220, overflow: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    {data.create.map((c) => {
                      const id = String(c.trans_id);
                      return (
                        <Box key={id} onClick={() => toggleOne(id)} sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, py: 0.6, borderBottom: 1, borderColor: 'divider', cursor: 'pointer', bgcolor: selected.has(id) ? 'action.hover' : 'transparent' }}>
                          <Checkbox size="small" sx={{ p: 0.5 }} checked={selected.has(id)} onChange={() => toggleOne(id)} onClick={(e) => e.stopPropagation()} />
                          <Typography variant="caption" sx={{ flex: 1 }}>{c.date} · <span style={{ fontFamily: 'monospace' }}>{c.trans_id}</span> · pay {c.pay_num ?? '?'}</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>{fmtMoney(c.amount)}{c.fee > 0.005 ? ` (fee ${fmtMoney(c.fee)})` : ''}</Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </>
              )}
              {nothingToDo && <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}><Typography variant="caption">Nothing to backfill — every charge is already booked or linked.</Typography></Box>}
            </Box>
          ) : err ? <Alert severity="error">{err}</Alert> : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{result ? 'Close' : 'Cancel'}</Button>
        <Box sx={{ flex: 1 }} />
        {!result && data && data.create.length > 0 && (
          <Button variant="contained" color="success" onClick={run} disabled={running || !!linkingTx || selected.size === 0}>{running ? 'Creating…' : `Create ${selected.size} order${selected.size === 1 ? '' : 's'}`}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function FailureDialog({ sub, rows, onClose }) {
  if (!sub) return null;
  const list = rows || [];
  return (
    <Dialog open={!!sub} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Failed charges — {sub.name || sub.donor_name || sub.id}
        <IconButton onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>{ACCOUNT_LABEL[sub.account] || sub.account} · subscription {sub.id} · {list.length} failed attempt{list.length === 1 ? '' : 's'}</Typography>
        {list.length === 0 ? <Typography variant="body2" color="text.secondary">No recorded failures for this subscription.</Typography>
          : (
            <Table size="small">
              <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Pay #</TableCell><TableCell align="right">Amount</TableCell><TableCell>Reason</TableCell></TableRow></TableHead>
              <TableBody>
                {list.map((f, i) => (
                  <TableRow key={i}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{f.submitted_at ? new Date(f.submitted_at).toLocaleDateString() : '—'}</TableCell>
                    <TableCell>{f.pay_num}{f.attempt_num > 1 ? ` · try ${f.attempt_num}` : ''}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(f.amount_num)}</TableCell>
                    <TableCell><Typography variant="caption">{f.response || '—'}</Typography></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

// Self-contained "check failed charges" header action — holds its own running/modal state so the config
// can stay declarative.
function CheckFailuresAction({ account, loading, onRefresh }) {
  const [running, setRunning] = useState(false);
  const [dlg, setDlg] = useState('');
  const [err, setErr] = useState('');
  const run = async () => {
    if (running) return; setRunning(true); setErr('');
    try {
      const r = await post('/api/manage/authnet-subscription-failures/run', { account, status: 'all' });
      setDlg(r?.message || (r?.started ? 'Checking failed charges across every subscription. This runs in the background and takes a minute or two — click “Refresh now” to load the latest results.' : 'A failure check is already running in the background. Click “Refresh now” in a minute or two to see results.'));
    } catch (e) { setErr(e?.response?.data?.error || e?.message || 'Failure check failed'); }
    finally { setRunning(false); }
  };
  return (
    <>
      <Tooltip title="Check failed charges across every subscription">
        <span>
          <IconButton size="small" color="error" onClick={run} disabled={running || loading} sx={{ border: 1, borderColor: 'error.light', borderRadius: 1 }}>
            {running ? <CircularProgress size={16} color="inherit" /> : <WarningAmberIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>
      <Dialog open={!!dlg || !!err} onClose={() => { setDlg(''); setErr(''); }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CircularProgress size={18} /> Checking failed charges</DialogTitle>
        <DialogContent>{err ? <Alert severity="error">{err}</Alert> : <Typography variant="body2">{dlg}</Typography>}</DialogContent>
        <DialogActions>
          <Button onClick={() => { setDlg(''); setErr(''); }}>Close</Button>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={() => { setDlg(''); onRefresh(); }}>Refresh now</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}


export const authnetSubsConfig = {
  provider: 'authnet',
  title: 'Subscriptions',
  accounts: true,
  defaultStatus: 'all',
  blurb: 'Live from Authorize.Net (both merchant accounts).',
  syncTitle: 'Sync now — reload subscriptions from Authorize.Net',
  buildNav: (active, account, navigate) => buildAuthnetNav('subscriptions', account, navigate),
  load: (fresh) => get('/api/manage/authnet-subscriptions', fresh === true ? { fresh: 1 } : {}),
  loadMaps: async () => (await get('/api/manage/authnet-subscriptions/maps'))?.mappings || [],
  loadExtra: () => get('/api/manage/authnet-subscription-failures'),
  mapKeyOfMapping: (m) => `${m.account}:${m.subscription_id}`,
  mapKey: (s) => `${s.account}:${s.id}`,
  rowKey: (s) => `${s.account}:${s.id}`,
  unmap: (s) => post('/api/manage/authnet-subscriptions/unmap', { account: s.account, subscription_id: s.id }),
  searchText: (s) => `${s.name || ''} ${s.donor_name || ''} ${s.id} ${s.amount} ${s.mapping?.customer_name || ''} ${(s.mapping?.splits || []).map((x) => x.label).join(' ')}`,
  statusOptions: STATUS_OPTIONS,
  matchesStatus: (s, statusFilter) => (statusFilter === 'all' ? true : normStatus(s.status) === statusFilter),
  // The shell renders the shared tiles + columns; this just maps an Authorize.Net subscription to the common shape.
  normalize: (s, { extra }) => {
    const exp = normStatus(s.status) === 'expired';
    return {
      name: s.name || null,
      amount: Number(s.amount) || 0,
      status: statusChipLabel(s.status),
      statusColor: statusChipColor(s.status),
      active: String(s.status).toLowerCase() === 'active',
      expired: exp,
      expiredNeedsReview: exp && !expiredInfo(s).complete,
      paidCount: s.stats?.paid_count ?? s.past_occurrences ?? null,
      linkedCount: s.stats?.linked_count ?? 0,
      needsLinkCount: s.stats?.needs_link_count ?? 0,
      unbookedCount: s.stats?.unbooked_count ?? 0,
      failedCount: failCount(s, { extra }),
      nextPaymentDate: s.next_payment_date || null,
      installments: occLabel(s),
    };
  },
  detailSlot: 'history',
  failureSlot: 'failure',
  mapApi: authnetMapApi,
  onMapped: (savedSub, ctx) => { if (savedSub) ctx.openSlot('backfill', savedSub); },
  headerActions: (ctx) => <CheckFailuresAction account={ctx.account} loading={ctx.loading} onRefresh={() => ctx.reload()} />,
  renderDialogs: (ctx) => (
    <>
      <PaymentHistoryDialog sub={ctx.slots.history} onClose={() => ctx.closeSlot('history')} />
      <BackfillDialog sub={ctx.slots.backfill} onClose={() => ctx.closeSlot('backfill')} onDone={() => { ctx.reloadMaps(); ctx.setToast('Orders created'); }} />
      <FailureDialog sub={ctx.slots.failure} rows={ctx.slots.failure ? failRowsFor(ctx.slots.failure, ctx) : []} onClose={() => ctx.closeSlot('failure')} />
    </>
  ),
};

export default function AuthnetSubscriptionsNewPage() {
  return <SubscriptionsWorkspace config={authnetSubsConfig} />;
}
